import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  Wrench, Calendar, ClipboardList, FileText, Settings, Plus, Trash2,
  Download, BarChart3, CheckCircle, Clock, AlertTriangle, PauseCircle, History, TrendingUp, Mail,
  DollarSign, Package
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

const STADIU_CONFIG = {
  PLANIFICAT: { label: 'Planificat', color: 'bg-blue-100 text-blue-700', chartColor: '#3b82f6' },
  'IN LUCRU': { label: 'In Lucru', color: 'bg-amber-100 text-amber-700', chartColor: '#f59e0b' },
  FINALIZAT: { label: 'Finalizat', color: 'bg-emerald-100 text-emerald-700', chartColor: '#10b981' },
  AMANAT: { label: 'Amanat', color: 'bg-red-100 text-red-700', chartColor: '#ef4444' },
};

const PMDashboardPage = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    responsabil_nume: '', responsabil_functie: 'RESPONSABIL MENTENANTA UTILAJELOR',
    cod_document: 'F-38-07', editia: '5', revizia: '0',
    alerte_enabled: true, destinatari: []
  });
  const [newDest, setNewDest] = useState({ name: '', email: '', phone: '' });
  const [recentHistory, setRecentHistory] = useState([]);
  const [yearComparison, setYearComparison] = useState(null);
  const [costReport, setCostReport] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, historyRes, compRes, costRes] = await Promise.all([
        api.get('/pm/stats', { params: { anul: selectedYear } }),
        api.get('/pm/history', { params: { anul: selectedYear } }).catch(() => ({ data: [] })),
        api.get('/pm/history', { params: { anul: selectedYear - 1 } }).catch(() => ({ data: [] })),
        api.get('/pm/cost-report', { params: { anul: selectedYear } }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setRecentHistory(historyRes.data.slice(0, 10));
      setCostReport(costRes.data);
      // Calculate simple year comparison
      const prevFinalized = compRes.data?.length || 0;
      const currFinalized = statsRes.data?.finalizat || 0;
      setYearComparison({
        current_year: selectedYear,
        previous_year: selectedYear - 1,
        current_finalized: currFinalized,
        previous_finalized: prevFinalized,
        current_total: statsRes.data?.total || 0,
        current_rate: statsRes.data?.total > 0 ? Math.round(currFinalized / statsRes.data.total * 100) : 0
      });
    } catch (error) {
      toast.error('Eroare la incarcarea statisticilor');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/pm/settings');
      setSettings(res.data);
    } catch {}
  }, []);

  // Real-time: refresh PM dashboard on changes
  useRealtimeEvent('pm_change', fetchStats);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSaveSettings = async () => {
    try {
      await api.post('/pm/settings', settings);
      toast.success('Setari salvate cu succes!');
      setShowSettings(false);
    } catch (error) {
      toast.error('Eroare la salvare setari');
    }
  };

  const addDestinatari = () => {
    if (!newDest.name || !newDest.email) { toast.error('Nume si email sunt obligatorii'); return; }
    setSettings(prev => ({
      ...prev,
      destinatari: [...(prev.destinatari || []), { ...newDest }]
    }));
    setNewDest({ name: '', email: '', phone: '' });
  };

  const removeDestinatari = (idx) => {
    setSettings(prev => ({
      ...prev,
      destinatari: prev.destinatari.filter((_, i) => i !== idx)
    }));
  };

  const chartData = stats ? [
    { name: 'Planificat', value: stats.planificat, color: STADIU_CONFIG.PLANIFICAT.chartColor },
    { name: 'In Lucru', value: stats.in_lucru, color: STADIU_CONFIG['IN LUCRU'].chartColor },
    { name: 'Finalizat', value: stats.finalizat, color: STADIU_CONFIG.FINALIZAT.chartColor },
    { name: 'Amanat', value: stats.amanat, color: STADIU_CONFIG.AMANAT.chartColor },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="pm-dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pm-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Plan de mentenanta pentru anul {selectedYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-32 h-10" data-testid="pm-year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(stats?.available_years || [new Date().getFullYear()]).map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasRole('admin', 'manager') && (
            <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="pm-settings-btn">
              <Settings className="w-4 h-4 mr-2" />SETARI
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white">
          <CardContent className="p-4 text-center">
            <Wrench className="w-7 h-7 text-slate-600 mx-auto mb-1" />
            <p className="text-3xl font-bold text-slate-800" data-testid="pm-stat-total">{stats?.total || 0}</p>
            <p className="text-xs text-slate-500 font-medium">TOTAL UTILAJE</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4 text-center">
            <Calendar className="w-7 h-7 text-blue-600 mx-auto mb-1" />
            <p className="text-3xl font-bold text-blue-700" data-testid="pm-stat-planificat">{stats?.planificat || 0}</p>
            <p className="text-xs text-blue-600 font-medium">PLANIFICATE</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4 text-center">
            <Clock className="w-7 h-7 text-amber-600 mx-auto mb-1" />
            <p className="text-3xl font-bold text-amber-700" data-testid="pm-stat-inlucru">{stats?.in_lucru || 0}</p>
            <p className="text-xs text-amber-600 font-medium">IN LUCRU</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-7 h-7 text-emerald-600 mx-auto mb-1" />
            <p className="text-3xl font-bold text-emerald-700" data-testid="pm-stat-finalizat">{stats?.finalizat || 0}</p>
            <p className="text-xs text-emerald-600 font-medium">FINALIZATE</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <CardContent className="p-4 text-center">
            <PauseCircle className="w-7 h-7 text-red-600 mx-auto mb-1" />
            <p className="text-3xl font-bold text-red-700" data-testid="pm-stat-amanat">{stats?.amanat || 0}</p>
            <p className="text-xs text-red-600 font-medium">AMANATE</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart + Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />Progres Lunar - Planificate vs Finalizate ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.monthly_bar || []).some(m => m.planificate > 0) ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.monthly_bar} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="luna" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="planificate" name="Planificate" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="finalizate" name="Finalizate" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="intarziate" name="Intarziate" fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-40" />
                <p>Nu exista date lunare pentru anul {selectedYear}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-700">Actiuni rapide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full justify-start h-11 bg-[#0052CC] hover:bg-[#003d99]"
                    onClick={() => navigate('/maintenance/preventive/plan')} data-testid="pm-goto-plan-btn">
              <ClipboardList className="w-4 h-4 mr-2" />Plan Mentenanta
            </Button>
            <Button className="w-full justify-start h-11" variant="outline"
                    onClick={() => navigate('/settings/pm-procedures')} data-testid="pm-goto-procedures-btn">
              <FileText className="w-4 h-4 mr-2" />Nomenclator Proceduri
            </Button>
            <Separator />
            <Button className="w-full justify-start h-11" variant="outline"
                    onClick={() => navigate('/maintenance/preventive/plan?action=pdf')} data-testid="pm-generate-pdf-btn">
              <Download className="w-4 h-4 mr-2" />Genereaza plan de mentenanta PDF
            </Button>
            <Button className="w-full justify-start h-11" variant="outline"
                    onClick={() => navigate('/maintenance/preventive/plan?action=export')} data-testid="pm-export-excel-btn">
              <FileText className="w-4 h-4 mr-2" />Export Excel
            </Button>
            {/* Pie chart mini */}
            {chartData.length > 0 && (
              <>
                <Separator />
                <div className="pt-1">
                  <p className="text-xs font-medium text-slate-500 mb-2">Distributie pe stadiu</p>
                  <ResponsiveContainer width="100%" height={140}>
                    <PieChart>
                      <Pie data={chartData} cx="50%" cy="50%" innerRadius={30} outerRadius={55}
                           paddingAngle={3} dataKey="value">
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Overdue Alert Banner */}
      {(stats?.overdue_count || 0) > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-red-800">
                {stats.overdue_count} mentenante intarziate/amanate
              </p>
              <p className="text-sm text-red-600">
                Exista echipamente cu mentenanta amanata sau reprogramata care necesita atentie imediata.
              </p>
            </div>
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100"
                    onClick={() => navigate('/maintenance/preventive/plan')}
                    data-testid="pm-overdue-btn">
              Vizualizeaza
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cost Report Section */}
      {costReport && (costReport.total_cost > 0 || costReport.total_consumabile_types > 0) && (
        <div className="space-y-4" data-testid="pm-cost-section">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* KPI: Total Cost */}
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-800" data-testid="pm-cost-total">
                      {costReport.total_cost?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} RON
                    </p>
                    <p className="text-xs text-indigo-500 font-medium">CHELTUIELI TOTALE MATERIALE ({selectedYear})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Wrench className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{costReport.total_equipment_with_costs}</p>
                    <p className="text-xs text-slate-500 font-medium">ECHIPAMENTE CU COSTURI</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Package className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{costReport.total_consumabile_types}</p>
                    <p className="text-xs text-slate-500 font-medium">TIPURI CONSUMABILE FOLOSITE</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Cost Chart + Top Consumables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />Cheltuieli Lunare Materiale ({selectedYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costReport.monthly_costs?.some(m => m.cost > 0) ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={costReport.monthly_costs} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="luna" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v > 0 ? `${v.toLocaleString()}` : '0'} />
                      <Tooltip formatter={(value) => [`${Number(value).toLocaleString('ro-RO', { minimumFractionDigits: 2 })} RON`, 'Cost']} />
                      <Area type="monotone" dataKey="cost" stroke="#6366f1" fill="url(#costGradient)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Nu exista cheltuieli inregistrate pentru {selectedYear}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Package className="w-4 h-4" />Top Consumabile Folosite
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costReport.top_consumabile?.length > 0 ? (
                  <div className="overflow-y-auto max-h-[220px]">
                    <table className="w-full text-xs" data-testid="pm-top-consumables-table">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="text-left p-1.5 font-medium text-slate-500">Denumire</th>
                          <th className="text-right p-1.5 font-medium text-slate-500">Cant.</th>
                          <th className="text-right p-1.5 font-medium text-slate-500">Cost (RON)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costReport.top_consumabile.map((c, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            <td className="p-1.5 font-medium text-slate-700 truncate max-w-[200px]" title={c.denumire}>
                              {c.denumire}
                            </td>
                            <td className="p-1.5 text-right text-slate-600">{c.cantitate} {c.um}</td>
                            <td className="p-1.5 text-right font-semibold text-indigo-700">
                              {c.cost?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Nu exista consumabile inregistrate</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Equipment Costs Table */}
          {costReport.equipment_costs?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />Cheltuieli pe Echipament
                  <Badge variant="secondary" className="ml-2">{costReport.total_equipment_with_costs} echipamente</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="pm-equipment-costs-table">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-2 font-medium text-slate-600">Nr.</th>
                        <th className="text-left p-2 font-medium text-slate-600">Echipament</th>
                        <th className="text-left p-2 font-medium text-slate-600">Serie</th>
                        <th className="text-left p-2 font-medium text-slate-600">Locatie</th>
                        <th className="text-left p-2 font-medium text-slate-600">Stadiu</th>
                        <th className="text-left p-2 font-medium text-slate-600">Consumabile</th>
                        <th className="text-right p-2 font-medium text-slate-600">Cost (RON)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costReport.equipment_costs.map((eq, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50">
                          <td className="p-2 text-slate-500">{i + 1}</td>
                          <td className="p-2 font-medium">{eq.denumire_utilaj}</td>
                          <td className="p-2 text-slate-600 font-mono text-xs">{eq.serie}</td>
                          <td className="p-2 text-slate-600">{eq.locatie}</td>
                          <td className="p-2">
                            {eq.stadiu && (
                              <Badge className={STADIU_CONFIG[eq.stadiu]?.color || 'bg-slate-100 text-slate-700'}>
                                {STADIU_CONFIG[eq.stadiu]?.label || eq.stadiu}
                              </Badge>
                            )}
                          </td>
                          <td className="p-2 text-slate-600">{eq.nr_consumabile} tipuri</td>
                          <td className="p-2 text-right font-bold text-indigo-700">
                            {eq.cost?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recent History */}
      {recentHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <History className="w-4 h-4" />Istoric Mentenante Finalizate - {selectedYear}
              <Badge variant="secondary" className="ml-2">{stats?.history_count || 0} total</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pm-history-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-2 font-medium text-slate-600">Echipament</th>
                    <th className="text-left p-2 font-medium text-slate-600">Serie</th>
                    <th className="text-left p-2 font-medium text-slate-600">Perioada</th>
                    <th className="text-left p-2 font-medium text-slate-600">Rezultat</th>
                    <th className="text-left p-2 font-medium text-slate-600">Finalizat de</th>
                    <th className="text-left p-2 font-medium text-slate-600">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {recentHistory.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="p-2 font-medium">{item.denumire_utilaj}</td>
                      <td className="p-2 text-slate-600 font-mono text-xs">{item.serie}</td>
                      <td className="p-2 text-slate-600">{item.perioada_programata}</td>
                      <td className="p-2">
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {item.rezultat_mentenanta || 'FINALIZAT'}
                        </Badge>
                      </td>
                      <td className="p-2 text-slate-600">{item.finalizat_de_name || '-'}</td>
                      <td className="p-2 text-slate-500 text-xs">
                        {item.finalizat_la ? new Date(item.finalizat_la).toLocaleDateString('ro-RO') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year Comparison */}
      {yearComparison && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />Comparativ An-la-An: {yearComparison.previous_year} vs {yearComparison.current_year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-slate-50 border">
                <p className="text-xs text-slate-500 font-medium mb-1">{yearComparison.previous_year} - Finalizate</p>
                <p className="text-3xl font-bold text-slate-600">{yearComparison.previous_finalized}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">{yearComparison.current_year} - Finalizate</p>
                <p className="text-3xl font-bold text-blue-700">{yearComparison.current_finalized}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-xs text-emerald-600 font-medium mb-1">Rata completare {yearComparison.current_year}</p>
                <p className="text-3xl font-bold text-emerald-700">{yearComparison.current_rate}%</p>
              </div>
              <div className="text-center p-4 rounded-lg border" style={{ background: yearComparison.current_finalized >= yearComparison.previous_finalized ? '#f0fdf4' : '#fef2f2', borderColor: yearComparison.current_finalized >= yearComparison.previous_finalized ? '#bbf7d0' : '#fecaca' }}>
                <p className="text-xs font-medium mb-1" style={{ color: yearComparison.current_finalized >= yearComparison.previous_finalized ? '#16a34a' : '#dc2626' }}>Diferenta</p>
                <p className="text-3xl font-bold" style={{ color: yearComparison.current_finalized >= yearComparison.previous_finalized ? '#16a34a' : '#dc2626' }}>
                  {yearComparison.current_finalized >= yearComparison.previous_finalized ? '+' : ''}{yearComparison.current_finalized - yearComparison.previous_finalized}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Equipment Table Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Wrench className="w-4 h-4" />Echipamente in plan - {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(stats?.equipment_list || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pm-dashboard-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-2 font-medium text-slate-600">Nr.</th>
                    <th className="text-left p-2 font-medium text-slate-600">Denumire utilaj</th>
                    <th className="text-left p-2 font-medium text-slate-600">Utilizare</th>
                    <th className="text-left p-2 font-medium text-slate-600">Locatie</th>
                    <th className="text-left p-2 font-medium text-slate-600">Perioada</th>
                    <th className="text-left p-2 font-medium text-slate-600">Stadiu</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.equipment_list.map((item, idx) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate('/maintenance/preventive/plan')}>
                      <td className="p-2 text-slate-500">{idx + 1}</td>
                      <td className="p-2 font-medium">{item.denumire_utilaj}</td>
                      <td className="p-2 text-slate-600">{item.utilizare}</td>
                      <td className="p-2 text-slate-600">{item.locatie}</td>
                      <td className="p-2 text-slate-600">{item.perioada_programata}</td>
                      <td className="p-2">
                        <Badge className={STADIU_CONFIG[item.stadiu]?.color || 'bg-slate-100'}>
                          {STADIU_CONFIG[item.stadiu]?.label || item.stadiu}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Wrench className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>Nu exista echipamente in plan pentru {selectedYear}</p>
              <Button className="mt-3" size="sm" onClick={() => navigate('/maintenance/preventive/plan')}>
                <Plus className="w-4 h-4 mr-1" />Adauga echipamente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Setari Mentenanta Preventiva</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Responsabil Section */}
            <div className="p-4 rounded-lg border bg-slate-50 space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Responsabil mentenanta</p>
              <div className="space-y-2">
                <Label>Nume complet</Label>
                <Input value={settings.responsabil_nume} className="h-11"
                       onChange={e => setSettings(p => ({ ...p, responsabil_nume: e.target.value.toUpperCase() }))}
                       placeholder="ex: DUTU VALENTIN" data-testid="pm-settings-name" />
              </div>
              <div className="space-y-2">
                <Label>Functie</Label>
                <Input value={settings.responsabil_functie} className="h-11"
                       onChange={e => setSettings(p => ({ ...p, responsabil_functie: e.target.value.toUpperCase() }))}
                       data-testid="pm-settings-role" />
              </div>
            </div>

            {/* Alerte & Programare Section */}
            <div className="p-4 rounded-lg border space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Alerte si notificari</p>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <input type="checkbox"
                  checked={settings.alerte_enabled !== false}
                  onChange={e => setSettings(p => ({ ...p, alerte_enabled: e.target.checked }))}
                  className="rounded" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Activeaza alerte email</p>
                  <p className="text-xs text-slate-500">Trimite automat alerte pentru mentenante intarziate</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Alerteaza cu N zile inainte</Label>
                  <Select value={String(settings.alert_days_before || 14)} onValueChange={v => setSettings(p => ({ ...p, alert_days_before: Number(v) }))}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[7, 14, 21, 30].map(d => <SelectItem key={d} value={String(d)}>{d} zile</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Frecventa verificare</Label>
                  <Select value={settings.alert_frequency || 'zilnic'} onValueChange={v => setSettings(p => ({ ...p, alert_frequency: v }))}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zilnic">Zilnic</SelectItem>
                      <SelectItem value="saptamanal">Saptamanal</SelectItem>
                      <SelectItem value="lunar">Lunar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="pt-2">
                <Button variant="outline" className="w-full h-10 text-sm" onClick={() => { setShowSettings(false); navigate('/reports/scheduled'); }}>
                  <Mail className="w-4 h-4 mr-2" />Gestioneaza rapoarte programate avansate
                </Button>
              </div>
            </div>

            <Separator />
            <div>
              <Label className="mb-2 block font-semibold">Destinatari alerte email</Label>
              {(settings.destinatari || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2 mb-2 text-sm bg-slate-50 p-2 rounded">
                  <span className="flex-1">{d.name} - {d.email} {d.phone && `(${d.phone})`}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeDestinatari(i)}>
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input placeholder="Nume" value={newDest.name} className="h-9 text-sm"
                       onChange={e => setNewDest(p => ({ ...p, name: e.target.value }))} />
                <Input placeholder="Email" value={newDest.email} className="h-9 text-sm"
                       onChange={e => setNewDest(p => ({ ...p, email: e.target.value }))} />
                <div className="flex gap-1">
                  <Input placeholder="Telefon" value={newDest.phone} className="h-9 text-sm flex-1"
                         onChange={e => setNewDest(p => ({ ...p, phone: e.target.value }))} />
                  <Button size="sm" onClick={addDestinatari} className="h-9"><Plus className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
            <Button className="w-full h-11 bg-[#0052CC]" onClick={handleSaveSettings} data-testid="pm-save-settings-btn">
              SALVEAZA SETARI
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PMDashboardPage;
